import React, { FC, useCallback } from 'react';
import { useStaticPermission } from 'common/auth/Authorize';
import { DataTableRowActions, DataTableRowAction } from '@utmost.co/utmost-design-system';
import { tokens } from '@beeline.com/bp-design-system';
import { CustomFieldDefinitionEntityTypeModel, StatusModel } from 'src/graphql/types';
import { useTypedMutation } from 'src/utils/hooks/useTypedMutation';
import { CustomFieldDefinition } from '../utils/CustomFields.types';
import { useCustomFieldsAndObjectsIntl } from '../../translations/useCustomFieldsAndObjectsIntl';
import { UpdateCustomFieldDefinitionDocument } from '../utils/__generated__/UpdateCustomFieldDefinition.graphql';

interface Props {
  onSubmitDone: () => void;
  onEdit: (customDefinition: CustomFieldDefinition) => void;
  onDelete: (customDefinition: CustomFieldDefinition) => void;
  customDefinition: CustomFieldDefinition;
}

export const CustomFieldDefinitionActions: FC<React.PropsWithChildren<Props>> = ({
  customDefinition,
  onEdit,
  onDelete,
  onSubmitDone
}) => {
  const intl = useCustomFieldsAndObjectsIntl();
  const { DELETE, UPDATE } = useStaticPermission('CUSTOM_FIELD_DEFINITION');

  const isActive = customDefinition.status === StatusModel.Active;

  const { set: toggleCustomFieldDefinitionStatus } = useTypedMutation(UpdateCustomFieldDefinitionDocument);

  const onStatusChangeHandler = useCallback(async () => {
    const response = await toggleCustomFieldDefinitionStatus({
      variables: {
        customFieldDefinition: { ...customDefinition, status: isActive ? StatusModel.Inactive : StatusModel.Active }
      }
    });
    if (!response.errors) {
      onSubmitDone();
    }
  }, [customDefinition, isActive, onSubmitDone, toggleCustomFieldDefinitionStatus]);

  return (
    <DataTableRowActions
      disabledMessage={intl.editInObjectsView}
      disabled={customDefinition.parentEntityType === CustomFieldDefinitionEntityTypeModel.CustomObject}
    >
      <DataTableRowAction
        dataTest="edit-custom-field-definition"
        onClick={() => onEdit(customDefinition)}
        disabledMessage={intl.noPermissionForAction}
        disabled={!UPDATE}
      >
        {intl.edit}
      </DataTableRowAction>
      {isActive ? (
        <DataTableRowAction
          dataTest="deactivate-custom-field-definition"
          onClick={onStatusChangeHandler}
          disabledMessage={intl.noPermissionForAction}
          disabled={!UPDATE}
        >
          {intl.deactivate}
        </DataTableRowAction>
      ) : (
        <DataTableRowAction
          dataTest="activate-custom-field-definition"
          onClick={onStatusChangeHandler}
          disabledMessage={intl.noPermissionForAction}
          disabled={!UPDATE}
        >
          {intl.activate}
        </DataTableRowAction>
      )}
      <DataTableRowAction
        dataTest="delete-custom-field-definition"
        style={{ color: tokens.colorAlert }}
        onClick={() => onDelete(customDefinition)}
        disabledMessage={intl.noPermissionForAction}
        disabled={!DELETE}
      >
        {intl.delete}
      </DataTableRowAction>
    </DataTableRowActions>
  );
};
